/**
 * FSD Part 1 — Enterprise (docs §19161), laid out as the FSD's own sections
 * (C17, FSD menu structure) and, since R0, item for item as the reference
 * prototype's menu (`docs/Enterprise_v4.html`, NAVG):
 *  - `p01-enterprise-overview` — the part's landing section (Dashboard & Inbox,
 *    which carries the go-live checklist).
 *  - `s-1.2.1` … `s-1.2.9` — one section per feature specification, FND-F01
 *    to FND-F09, in FSD order, each holding the reference menu's screens for
 *    that feature in the reference order (plus the screens the FSD specifies
 *    there that the prototype folds into another, e.g. elimination rules).
 *  - `s-1.1` — the Part 1 catalogue: the governance registers specified only
 *    as catalogue requirements (FND-FR-084 … FND-FR-090), each item carrying
 *    its requirement id as `code`.
 *
 * `p01-reference.test.ts` holds the reference menu as a table and fails when
 * any of its items has no screen here.
 *
 * Item ids are stable and never change with placement: favourites and recents
 * are keyed off `id` in localStorage, not off section or position. When an FSD
 * screen replaced a legacy one, the legacy item's id was KEPT and pointed at
 * the new route (R0): `org-go-live` → `/enterprise`, `org-fiscal-periods` →
 * `/enterprise/period-control`, `fx-rates` → `/enterprise/currency-rates`,
 * `enterprise-naming-templates` → `/enterprise/workforce/model` (which also
 * absorbed `enterprise-headcount`, dropped). The old paths redirect
 * (`router/legacyRedirects.ts`).
 *
 * Placement notes the FSD text decides:
 *  - `org-designations` and `org-filing-calendar` sit in 1.2.3 Legal Entities:
 *    statutory designations (FSD text line 1319) and the statutory filing
 *    calendar (FSD text line 1509) are both specified there.
 *  - `org-units` (`/settings/organization/hierarchy`) and
 *    `org-chart-of-accounts` (`/settings/organization/accounts`) are
 *    legacy-path screens kept because they are the only implementation of FSD
 *    scope (DECISIONS.md C17): the first ("Branches & Profit Centres") now
 *    maintains only branches and profit centres — departments and cost centres
 *    are maintained solely by `enterprise-departments` and
 *    `enterprise-cost-centres`;
 *    the second is the only screen that edits GL accounts and applies a COA
 *    template — the multi-segment COA reads its codes from that table.
 *    `org-units` is not `org-hierarchy` (`/enterprise/hierarchies`, 1.2.9).
 *  - currencies / FX rates / FX holds (from Part 3), the tax packs /
 *    jurisdictions / codes (from Part 2) and the authority matrix / metric
 *    dictionary / reason codes (from Part D) moved here because the FSD
 *    specifies them under Part 1.
 *  - `interco-pairs` (intercompany RECONCILIATION pairs) is a Finance concept
 *    and lives in Part 2's catalogue (`p02-finance.ts`, FIN-FR-030); 1.2.3's
 *    intercompany screen is `enterprise-intercompany`.
 */
import type { NavGroup } from '../types'
import { PERMISSIONS } from '@/lib/permissions'
import { OPCAL_PERMISSIONS } from '@/modules/opcal/permissions'
import { ORG_PERMISSIONS } from '@/modules/org/permissions'
import { TAX_SPINE_PERMISSIONS } from '@/modules/tax/permissions'
import { AUTHORITY_PERMISSIONS } from '@/modules/authority/permissions'
import { REPORTING_PERMISSIONS } from '@/modules/reporting/permissions'
import { RULES_PERMISSIONS } from '@/modules/rules/permissions'
import { ENTERPRISE_PERMISSIONS } from '@/modules/enterprise/setup/permissions'
import { LEGAL_ENTITY_PERMISSIONS } from '@/modules/enterprise/legal-entities/permissions'
import { BUSINESS_UNIT_PERMISSIONS } from '@/modules/enterprise/business-units/permissions'
import { DIVISION_PERMISSIONS } from '@/modules/enterprise/divisions/permissions'
import { HIERARCHY_PERMISSIONS } from '@/modules/enterprise/hierarchies/permissions'
import { REFERENCE_SET_PERMISSIONS } from '@/modules/enterprise/reference-sets/permissions'
import { COA_PERMISSIONS } from '@/modules/enterprise/financial-structures/permissions'
import { GOVERNANCE_PERMISSIONS } from '@/modules/enterprise/governance/permissions'
import { WORKFORCE_PERMISSIONS } from '@/modules/enterprise/workforce/permissions'
import { LOCALISATION_PERMISSIONS } from '@/modules/enterprise/localisation/permissions'
import { OVERVIEW_PERMISSIONS } from '@/modules/enterprise/overview/permissions'
import { LEDGER_PERMISSIONS } from '@/modules/enterprise/ledgers/permissions'
import { PERIOD_PERMISSIONS } from '@/modules/enterprise/periods/permissions'
import { CURRENCY_RATE_PERMISSIONS } from '@/modules/enterprise/currency-rates/permissions'
import { SIMULATOR_PERMISSIONS } from '@/modules/enterprise/simulator/permissions'
import { GEOGRAPHY_PERMISSIONS } from '@/modules/enterprise/geographies/permissions'

export const P01_ENTERPRISE_GROUPS: NavGroup[] = [
  {
    id: 'p01-enterprise-overview',
    kind: 'overview',
    labelKey: 'nav.fsd.overview',
    items: [
      {
        // Id kept from the legacy go-live checklist screen this dashboard
        // replaced (R0) — favourites point at it.
        id: 'org-go-live',
        labelKey: 'enterprise.overview.navLabel',
        to: '/enterprise',
        icon: 'dashboard',
        permission: OVERVIEW_PERMISSIONS.read,
        keywordsKey: 'nav.items.orgGoLive.keywords',
        descriptionKey: 'nav.items.orgGoLive.description',
        fsd: '1.2.1',
        code: 'FND-F01',
      },
    ],
  },
  {
    id: 's-1.2.1',
    kind: 'feature',
    fsd: '1.2.1',
    code: 'FND-F01',
    labelKey: 'nav.fsd.s1_2_1.short',
    fullTitleKey: 'nav.fsd.s1_2_1.full',
    items: [
      {
        id: 'enterprise-setup',
        labelKey: 'enterprise.setup.routeTitles.workspace',
        to: '/enterprise/setup',
        icon: 'settings',
        permission: ENTERPRISE_PERMISSIONS.enterpriseRead,
      },
    ],
  },
  {
    id: 's-1.2.2',
    kind: 'feature',
    fsd: '1.2.2',
    code: 'FND-F02',
    labelKey: 'nav.fsd.s1_2_2.short',
    fullTitleKey: 'nav.fsd.s1_2_2.full',
    items: [
      {
        id: 'org-divisions',
        labelKey: 'enterprise.divisions.routeTitles.list',
        to: '/enterprise/divisions',
        icon: 'sitemap',
        permission: DIVISION_PERMISSIONS.read,
      },
      {
        id: 'enterprise-cost-centres',
        labelKey: 'enterprise.divisions.routeTitles.costCentres',
        to: '/enterprise/divisions/cost-centres',
        icon: 'layers',
        permission: ORG_PERMISSIONS.unitRead,
      },
      {
        id: 'enterprise-cross-charges',
        labelKey: 'enterprise.divisions.routeTitles.crossCharges',
        to: '/enterprise/divisions/cross-charges',
        icon: 'link',
        permission: DIVISION_PERMISSIONS.crossChargeRead,
      },
      {
        id: 'enterprise-division-rollup',
        labelKey: 'enterprise.divisions.routeTitles.rollup',
        to: '/enterprise/divisions/rollup',
        icon: 'refresh',
        permission: DIVISION_PERMISSIONS.read,
      },
    ],
  },
  {
    id: 's-1.2.3',
    kind: 'feature',
    fsd: '1.2.3',
    code: 'FND-F03',
    labelKey: 'nav.fsd.s1_2_3.short',
    fullTitleKey: 'nav.fsd.s1_2_3.full',
    items: [
      {
        id: 'org-companies',
        labelKey: 'enterprise.legalEntities.routeTitles.list',
        to: '/enterprise/legal-entities',
        icon: 'building',
        permission: LEGAL_ENTITY_PERMISSIONS.read,
      },
      {
        id: 'enterprise-ownership',
        labelKey: 'enterprise.legalEntities.routeTitles.ownership',
        to: '/enterprise/legal-entities/ownership',
        icon: 'sitemap',
        permission: LEGAL_ENTITY_PERMISSIONS.ownershipRead,
      },
      {
        id: 'enterprise-intercompany',
        labelKey: 'enterprise.legalEntities.routeTitles.intercompany',
        to: '/enterprise/legal-entities/intercompany',
        icon: 'link',
        permission: LEGAL_ENTITY_PERMISSIONS.intercompanyRead,
      },
      {
        id: 'enterprise-elimination-rules',
        labelKey: 'enterprise.legalEntities.routeTitles.eliminationRules',
        to: '/enterprise/legal-entities/elimination-rules',
        icon: 'scale',
        permission: LEGAL_ENTITY_PERMISSIONS.intercompanyRead,
      },
      {
        id: 'org-designations',
        labelKey: 'org.routeTitles.designations',
        to: '/settings/organization/designations',
        icon: 'shieldCheck',
        permission: ORG_PERMISSIONS.designationRead,
        descriptionKey: 'nav.items.orgDesignations.description',
      },
      {
        id: 'org-filing-calendar',
        labelKey: 'org.routeTitles.filings',
        to: '/settings/organization/filing-calendar',
        icon: 'calendar',
        permission: ORG_PERMISSIONS.filingRead,
        descriptionKey: 'nav.items.orgFilingCalendar.description',
      },
    ],
  },
  {
    id: 's-1.2.4',
    kind: 'feature',
    fsd: '1.2.4',
    code: 'FND-F04',
    labelKey: 'nav.fsd.s1_2_4.short',
    fullTitleKey: 'nav.fsd.s1_2_4.full',
    items: [
      {
        id: 'enterprise-business-units',
        labelKey: 'enterprise.businessUnits.routeTitles.list',
        to: '/enterprise/business-units',
        icon: 'users',
        permission: BUSINESS_UNIT_PERMISSIONS.read,
      },
      {
        id: 'enterprise-service-providers',
        labelKey: 'enterprise.businessUnits.routeTitles.serviceProviders',
        to: '/enterprise/business-units/service-providers',
        icon: 'link',
        permission: BUSINESS_UNIT_PERMISSIONS.read,
      },
      {
        id: 'enterprise-security-scopes',
        labelKey: 'enterprise.businessUnits.routeTitles.securityScopes',
        to: '/enterprise/business-units/security-scopes',
        icon: 'shield',
        permission: BUSINESS_UNIT_PERMISSIONS.securityScope,
      },
    ],
  },
  {
    id: 's-1.2.5',
    kind: 'feature',
    fsd: '1.2.5',
    code: 'FND-F05',
    labelKey: 'nav.fsd.s1_2_5.short',
    fullTitleKey: 'nav.fsd.s1_2_5.full',
    items: [
      {
        // Id kept from the naming-templates screen; the Model, Naming &
        // Headcount screen absorbed it and the headcount screen (R0).
        id: 'enterprise-naming-templates',
        labelKey: 'enterprise.workforce.routeTitles.model',
        to: '/enterprise/workforce/model',
        icon: 'settings',
        // Mirrors the route's meta.permission (single-code gate; see
        // workforce/routes.ts for the any-of limitation).
        permission: WORKFORCE_PERMISSIONS.namingTemplateRead,
      },
      {
        id: 'enterprise-departments',
        labelKey: 'enterprise.workforce.routeTitles.departments',
        to: '/enterprise/workforce/departments',
        icon: 'sitemap',
        permission: ORG_PERMISSIONS.unitRead,
      },
      {
        id: 'org-jobs',
        labelKey: 'org.routeTitles.jobs',
        to: '/settings/organization/jobs',
        icon: 'building',
        permission: ORG_PERMISSIONS.jobRead,
        descriptionKey: 'nav.items.orgJobs.description',
      },
      {
        id: 'org-positions',
        labelKey: 'org.routeTitles.positions',
        to: '/settings/organization/positions',
        icon: 'sitemap',
        permission: ORG_PERMISSIONS.positionRead,
        descriptionKey: 'nav.items.orgPositions.description',
      },
      {
        id: 'org-job-grades',
        labelKey: 'org.routeTitles.grades',
        to: '/settings/organization/job-grades',
        icon: 'scale',
        permission: ORG_PERMISSIONS.jobRead,
        descriptionKey: 'nav.items.orgJobGrades.description',
      },
      {
        id: 'enterprise-assignment-validator',
        labelKey: 'enterprise.workforce.routeTitles.assignmentValidator',
        to: '/enterprise/workforce/assignment-validator',
        icon: 'check',
        permission: ORG_PERMISSIONS.assignmentRead,
      },
      {
        id: 'enterprise-reorganisations',
        labelKey: 'enterprise.workforce.routeTitles.reorganisations',
        to: '/enterprise/workforce/reorganisations',
        icon: 'refresh',
        permission: WORKFORCE_PERMISSIONS.reorganisationRead,
      },
      {
        // Branches & Profit Centres: the legacy-path screen kept ONLY for the
        // two unit kinds no FSD register maintains (FSD 1.2.5 / 1.2.2,
        // DECISIONS.md C17). It no longer offers departments or cost centres —
        // those are maintained only by `enterprise-departments` and
        // `enterprise-cost-centres` — and links to them instead.
        id: 'org-units',
        labelKey: 'org.hierarchy.unitsScreen.navLabel',
        to: '/settings/organization/hierarchy',
        icon: 'sitemap',
        permission: ORG_PERMISSIONS.unitRead,
        keywordsKey: 'org.hierarchy.unitsScreen.navKeywords',
        descriptionKey: 'org.hierarchy.unitsScreen.navDescription',
      },
    ],
  },
  {
    id: 's-1.2.6',
    kind: 'feature',
    fsd: '1.2.6',
    code: 'FND-F06',
    labelKey: 'nav.fsd.s1_2_6.short',
    fullTitleKey: 'nav.fsd.s1_2_6.full',
    items: [
      {
        id: 'enterprise-reference-sets',
        labelKey: 'enterprise.referenceSets.routeTitles.catalogue',
        to: '/enterprise/reference-sets',
        icon: 'globe',
        permission: REFERENCE_SET_PERMISSIONS.read,
      },
      {
        id: 'enterprise-reference-sets-determinants',
        labelKey: 'enterprise.referenceSets.routeTitles.determinants',
        to: '/enterprise/reference-sets/determinants',
        icon: 'sitemap',
        permission: REFERENCE_SET_PERMISSIONS.read,
      },
      {
        id: 'enterprise-reference-sets-browse',
        labelKey: 'enterprise.referenceSets.routeTitles.browse',
        to: '/enterprise/reference-sets/browse',
        icon: 'search',
        permission: REFERENCE_SET_PERMISSIONS.read,
      },
      {
        id: 'enterprise-reference-sets-compare',
        labelKey: 'enterprise.referenceSets.routeTitles.compare',
        to: '/enterprise/reference-sets/compare',
        icon: 'link',
        permission: REFERENCE_SET_PERMISSIONS.read,
      },
      {
        id: 'enterprise-reference-sets-visibility',
        labelKey: 'enterprise.referenceSets.routeTitles.visibility',
        to: '/enterprise/reference-sets/visibility',
        icon: 'shield',
        permission: REFERENCE_SET_PERMISSIONS.read,
      },
    ],
  },
  {
    id: 's-1.2.7',
    kind: 'feature',
    fsd: '1.2.7',
    code: 'FND-F07',
    labelKey: 'nav.fsd.s1_2_7.short',
    fullTitleKey: 'nav.fsd.s1_2_7.full',
    items: [
      {
        id: 'org-accounts',
        labelKey: 'enterprise.financialStructures.routeTitles.list',
        to: '/enterprise/financial-structures',
        icon: 'dashboard',
        permission: COA_PERMISSIONS.structureRead,
      },
      {
        id: 'enterprise-cvr-register',
        labelKey: 'enterprise.financialStructures.routeTitles.cvrRegister',
        to: '/enterprise/financial-structures/cross-validation-rules',
        icon: 'shieldCheck',
        permission: COA_PERMISSIONS.ruleRead,
      },
      {
        id: 'enterprise-value-sets',
        labelKey: 'enterprise.financialStructures.routeTitles.valueSetsList',
        to: '/enterprise/financial-structures/value-sets',
        icon: 'scale',
        permission: COA_PERMISSIONS.valueSetRead,
      },
      {
        // Legacy screen kept: the only screen that edits GL accounts and
        // applies a COA template; the multi-segment COA reads its codes from
        // that table. DECISIONS.md C17.
        id: 'org-chart-of-accounts',
        labelKey: 'nav.items.orgAccounts.label',
        to: '/settings/organization/accounts',
        icon: 'dashboard',
        permission: ORG_PERMISSIONS.accountRead,
        keywordsKey: 'nav.items.orgAccounts.keywords',
        descriptionKey: 'nav.items.orgAccounts.description',
      },
      {
        id: 'enterprise-accounting-calendars',
        labelKey: 'enterprise.ledgers.routeTitles.accountingCalendars',
        to: '/enterprise/accounting-calendars',
        icon: 'calendar',
        permission: LEDGER_PERMISSIONS.fiscalRead,
      },
      {
        id: 'enterprise-ledgers',
        labelKey: 'enterprise.ledgers.routeTitles.ledgers',
        to: '/enterprise/ledgers',
        icon: 'landmark',
        permission: LEDGER_PERMISSIONS.ledgerRead,
      },
      {
        id: 'enterprise-ledger-sets',
        labelKey: 'enterprise.ledgers.routeTitles.ledgerSets',
        to: '/enterprise/ledger-sets',
        icon: 'layers',
        permission: LEDGER_PERMISSIONS.ledgerSetRead,
      },
      {
        id: 'enterprise-accounting-methods',
        labelKey: 'enterprise.ledgers.routeTitles.accountingMethods',
        to: '/enterprise/accounting-methods',
        icon: 'settings',
        permission: LEDGER_PERMISSIONS.accountingMethodRead,
      },
      {
        // Id kept from the legacy fiscal-periods screen Period Control
        // replaced (R0).
        id: 'org-fiscal-periods',
        labelKey: 'enterprise.periods.routeTitles.control',
        to: '/enterprise/period-control',
        icon: 'clock',
        permission: PERIOD_PERMISSIONS.read,
        keywordsKey: 'nav.items.orgFiscalPeriods.keywords',
        descriptionKey: 'nav.items.orgFiscalPeriods.description',
      },
      {
        // Id kept from the legacy `/fx/rates` screen Currency Rates replaced (R0).
        id: 'fx-rates',
        labelKey: 'enterprise.currencyRates.routeTitles.list',
        to: '/enterprise/currency-rates',
        icon: 'history',
        permission: CURRENCY_RATE_PERMISSIONS.read,
        keywordsKey: 'nav.items.fxRates.keywords',
        descriptionKey: 'nav.items.fxRates.description',
      },
      {
        id: 'currencies',
        labelKey: 'nav.items.currencies.label',
        to: '/fx/currencies',
        icon: 'globe',
        permission: PERMISSIONS.fxCurrencyRead,
        keywordsKey: 'nav.items.currencies.keywords',
        descriptionKey: 'nav.items.currencies.description',
      },
      {
        id: 'fx-holds',
        labelKey: 'fx.hold.routeTitle',
        to: '/fx/holds',
        icon: 'alert',
        permission: 'fx.hold.read',
        descriptionKey: 'nav.items.fxHolds.description',
      },
      {
        id: 'org-opening-balances',
        labelKey: 'nav.items.orgOpeningBalances.label',
        to: '/settings/organization/opening-balances',
        icon: 'download',
        permission: ORG_PERMISSIONS.openingBalanceRead,
        keywordsKey: 'nav.items.orgOpeningBalances.keywords',
        descriptionKey: 'nav.items.orgOpeningBalances.description',
      },
      {
        // FSD cross-feature screen (SCR-X-SIM, FND-X-DET): plays a posting
        // module across 1.2.7, 1.2.3 and 1.2.4. Filed under 1.2.7 as the
        // reference menu does.
        id: 'enterprise-determination-simulator',
        labelKey: 'enterprise.simulator.routeTitles.simulator',
        to: '/enterprise/determination-simulator',
        icon: 'search',
        permission: SIMULATOR_PERMISSIONS.read,
        related: ['1.2.3', '1.2.4'],
      },
    ],
  },
  {
    id: 's-1.2.8',
    kind: 'feature',
    fsd: '1.2.8',
    code: 'FND-F08',
    labelKey: 'nav.fsd.s1_2_8.short',
    fullTitleKey: 'nav.fsd.s1_2_8.full',
    items: [
      {
        id: 'org-locations',
        labelKey: 'org.routeTitles.locations',
        to: '/settings/organization/locations',
        icon: 'mapPin',
        permission: ORG_PERMISSIONS.locationRead,
        descriptionKey: 'nav.items.orgLocations.description',
      },
      {
        id: 'enterprise-geographies',
        labelKey: 'enterprise.geographies.routeTitles.geographies',
        to: '/enterprise/geographies',
        icon: 'globe',
        permission: GEOGRAPHY_PERMISSIONS.read,
      },
      {
        id: 'tax-packs',
        labelKey: 'tax.spine.routeTitles.packs',
        to: '/tax/packs',
        icon: 'download',
        permission: TAX_SPINE_PERMISSIONS.packRead,
      },
      {
        id: 'tax-jurisdictions',
        labelKey: 'tax.spine.routeTitles.jurisdictions',
        to: '/tax/jurisdictions',
        icon: 'globe',
        permission: TAX_SPINE_PERMISSIONS.jurisdictionRead,
      },
      {
        id: 'tax-codes',
        labelKey: 'nav.items.taxCodes.label',
        to: '/tax/codes',
        icon: 'settings',
        permission: PERMISSIONS.taxCodeRead,
        keywordsKey: 'nav.items.taxCodes.keywords',
        descriptionKey: 'nav.items.taxCodes.description',
      },
      {
        id: 'enterprise-einvoicing',
        labelKey: 'enterprise.localisation.routeTitles.einvoicing',
        to: '/enterprise/localisation/einvoicing',
        icon: 'mail',
        permission: LOCALISATION_PERMISSIONS.einvoicingRead,
      },
      {
        id: 'enterprise-sandbox-runs',
        labelKey: 'enterprise.localisation.routeTitles.sandboxRuns',
        to: '/enterprise/localisation/sandbox-runs',
        icon: 'history',
        permission: LOCALISATION_PERMISSIONS.sandboxRunRead,
      },
      {
        id: 'enterprise-statutory-languages',
        labelKey: 'enterprise.localisation.routeTitles.statutoryLanguages',
        to: '/enterprise/localisation/statutory-languages',
        icon: 'globe',
        permission: LOCALISATION_PERMISSIONS.statutoryLanguageRead,
      },
    ],
  },
  {
    id: 's-1.2.9',
    kind: 'feature',
    fsd: '1.2.9',
    code: 'FND-F09',
    labelKey: 'nav.fsd.s1_2_9.short',
    fullTitleKey: 'nav.fsd.s1_2_9.full',
    items: [
      {
        id: 'org-hierarchy',
        labelKey: 'enterprise.hierarchies.routeTitles.register',
        to: '/enterprise/hierarchies',
        icon: 'sitemap',
        permission: HIERARCHY_PERMISSIONS.read,
      },
    ],
  },
  {
    id: 's-1.1',
    kind: 'catalogue',
    fsd: '1.1',
    labelKey: 'nav.fsd.s1_1.short',
    fullTitleKey: 'nav.fsd.s1_1.full',
    items: [
      {
        id: 'authority-matrix',
        labelKey: 'authority.routeTitles.matrix',
        to: '/authority/matrix',
        icon: 'shieldCheck',
        permission: AUTHORITY_PERMISSIONS.matrixRead,
        descriptionKey: 'nav.items.authorityMatrix.description',
        code: 'FND-FR-084',
      },
      {
        id: 'opcal-calendars',
        labelKey: 'opcal.routeTitles.calendars',
        to: '/settings/operating-calendars',
        icon: 'clock',
        permission: OPCAL_PERMISSIONS.calendarRead,
        descriptionKey: 'nav.items.opcalCalendars.description',
        code: 'FND-FR-085',
      },
      {
        id: 'enterprise-governance-mastership',
        labelKey: 'enterprise.governance.routeTitles.mastership',
        to: '/enterprise/governance/mastership',
        icon: 'shieldCheck',
        permission: GOVERNANCE_PERMISSIONS.mastershipRead,
        code: 'FND-FR-086',
      },
      {
        id: 'reporting-metrics',
        labelKey: 'reporting.routeTitles.dictionary',
        to: '/reporting/metrics',
        icon: 'search',
        permission: REPORTING_PERMISSIONS.metricRead,
        keywordsKey: 'nav.items.reportingMetrics.keywords',
        descriptionKey: 'nav.items.reportingMetrics.description',
        code: 'FND-FR-087',
      },
      {
        id: 'rules-reason-codes',
        labelKey: 'rules.routeTitles.reasons',
        to: '/controls/reason-codes',
        icon: 'settings',
        permission: RULES_PERMISSIONS.reasonRead,
        descriptionKey: 'nav.items.rulesReasonCodes.description',
        code: 'FND-FR-088',
      },
      {
        id: 'enterprise-governance-dependencies',
        labelKey: 'enterprise.governance.routeTitles.dependencyPostures',
        to: '/enterprise/governance/dependency-postures',
        icon: 'link',
        permission: GOVERNANCE_PERMISSIONS.dependencyPostureRead,
        code: 'FND-FR-089',
      },
      {
        id: 'enterprise-governance-classifications',
        labelKey: 'enterprise.governance.routeTitles.classifications',
        to: '/enterprise/governance/classifications',
        icon: 'shield',
        permission: GOVERNANCE_PERMISSIONS.classificationRead,
        code: 'FND-FR-090',
      },
    ],
  },
]
